import { Box, Typography, Button, AppBar, CardMedia, Divider, IconButton } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ApplicationProps,
  nationaliyType,
  RiskProfileMaster,
} from '../../redux-store/types/api-types';
import { useHistory } from 'react-router';
import { RootStateType } from '../../redux-store/reducers';
import { Footer } from '../commonComponents';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { ApplicationDetailsCommonLayout } from '../ApplicationDetails/index';
import { Location } from 'history';
import {
  getApplicationDetails,
  getDocuments,
  isFormValidForSubmission,
} from '../../redux-store/actions/application';
import { showError } from '../../redux-store/actions/auth';
import { getNationalityList, riskProfileMasterData } from '../../redux-store/actions';
import { getStrategies } from '../../redux-store/actions/strategies';
import { GetStrategiesResponseBody } from '../../redux-store/types/strategies';
import { useCustodianData } from '../../utils/useCustodianData';
import { useValidationOrConfigHook } from '../../utils/validationOrConfigHook';
import { ACCOUNT_TYPE_OPTIONS, distributorMaster } from '../../utils/constant';
import { checkDPMSFlow, checkNDPMFlow, getKey } from '../../utils/utilityFunctions';

export default function InvestorApplicationBeforeFaceCapture({
  location,
}: {
  location: Location<{
    applicationId: string;
    applicantId: string;
  }>;
}): JSX.Element {
  const { applicationId = '', applicantId = '' } = location.state || {};
  const { referenceId } = useParams<{ referenceId: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const { investor, auth } = useSelector((store: RootStateType) => store);
  const [application, setApplication] = useState<ApplicationProps>();
  const [loading, setLoading] = useState(false);
  // const [fatcaDetails, setFatcaDetails] = useState(initialValues);
  const applicationDetailRef = useRef<HTMLDivElement>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const referenceDetail = useSelector((store: RootStateType) => store.refrenceIdReducer);
  const [custodianData] = useCustodianData();
  const validationOrConfigChecks = useValidationOrConfigHook();
  useEffect(() => {
    (async function () {
      try {
        const _application = (await dispatch(
          getApplicationDetails(applicationId)
        )) as unknown as ApplicationProps;
        setApplication(_application);
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    })();
    setLoading(true);
  }, []);

  useEffect(() => {
    const { token } = investor;
    if (!token || !referenceId) {
      history.push(`/investor-liveliness/${referenceId}/login`);
    }
  }, []);

  const accountTypeMaster = getKey(ACCOUNT_TYPE_OPTIONS);
  const provideTypeMaster = getKey(distributorMaster);

  return (
    <Box
      sx={{
        bgcolor: { xs: '', sm: 'rgba(238, 244, 251, 0.5)' },
      }}>
      <IconButton
        sx={{
          position: 'fixed',
          right: 0,
          bottom: 75,
          borderRadius: '5px  0 0 5px',
          '&,:hover': {
            bgcolor: 'primary.main',
          },
        }}
        onClick={() => applicationDetailRef.current?.scrollIntoView({ behavior: 'smooth' })}>
        <ArrowUpwardIcon fontSize="large" sx={{ color: 'common.white' }} />
      </IconButton>
      <AppBar position="fixed" elevation={0} sx={{ bgcolor: 'common.white' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'inherit',
            width: '9rem',
          }}>
          <CardMedia
            component="img"
            src="/images/Renaissance-Logo-02.jpg"
            alt="Logo"
            sx={{ maxWidth: '100%', maxHeight: '100%', m: 0.5 }}
          />
        </Box>
      </AppBar>
      <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
      <Box sx={{ py: 5, px: { xs: 5, sm: 10 } }} ref={applicationDetailRef}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            mb: 2,
            justifyContent: 'space-between',
            mt: 10,
          }}>
          <Typography sx={{ fontSize: 20, fontWeight: 600, color: 'primary.main', mb: 4 }}>
            Application Details
          </Typography>
          {application?.editable && (
            <IconButton>
              <CardMedia
                component="img"
                src="/images/edit-icon-outlined.svg"
                alt="Edit Icon"
                sx={{ width: 'unset' }}
                onClick={() =>
                  history.push(
                    `/investor-liveliness/edit-application/${referenceId}/distributor-details`,
                    {
                      id: application?.id,
                      applicant1ReferenceId: referenceId,
                    }
                  )
                }
              />
            </IconButton>
          )}
        </Box>
        {application && (
          <>
            <ApplicationDetailsCommonLayout
              loading={loading}
              application={application as ApplicationProps}
            />

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                sx={{
                  color: 'common.white',
                  minWidth: '200px',
                  mt: 4,
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
                onClick={async () => {
                  try {
                    setIsSubmitting(true);
                    const nationalitiesMdmsMasters = (await dispatch(
                      getNationalityList()
                    )) as unknown as nationaliyType;
                    let strategiesResponse: GetStrategiesResponseBody[] = [];

                    if (application?.distributor_id) {
                      strategiesResponse = (await dispatch(
                        getStrategies({
                          isActive: true,
                          isApplicableForDistributor: true,
                          applicableForType: provideTypeMaster.Distributor,
                          distributorId: application?.distributor_id,
                          isDPMSFlow: checkDPMSFlow(application?.accountType || ''),
                          applicableFlowType: checkDPMSFlow(application?.accountType || '')
                            ? accountTypeMaster.discretionary
                            : accountTypeMaster.non_discretionary,
                          isNDPMSFlow: checkNDPMFlow(application?.accountType || ''),
                        })
                      )) as unknown as GetStrategiesResponseBody[];
                    } else {
                      strategiesResponse = (await dispatch(
                        getStrategies({
                          isActive: true,
                          isApplicableForDirect: true,
                          applicableForType: provideTypeMaster.Direct,
                          isDPMSFlow: checkDPMSFlow(application?.accountType || ''),
                          applicableFlowType: checkDPMSFlow(application?.accountType || '')
                            ? accountTypeMaster.discretionary
                            : accountTypeMaster.non_discretionary,
                          isNDPMSFlow: checkNDPMFlow(application?.accountType || ''),
                        })
                      )) as unknown as GetStrategiesResponseBody[];
                    }
                    const responseRiskProfile = (await dispatch(
                      riskProfileMasterData()
                    )) as unknown as RiskProfileMaster;
                    const response = (await dispatch(getDocuments())) as unknown as Document;
                    await isFormValidForSubmission(
                      application,
                      true,
                      true,
                      nationalitiesMdmsMasters,
                      custodianData,
                      validationOrConfigChecks,
                      response,
                      auth.role,
                      responseRiskProfile.riskProfile,
                      strategiesResponse,
                      referenceDetail
                    );
                    setIsSubmitting(false);
                    history.push(`location-capture`, { applicantId });
                  } catch (e) {
                    setIsSubmitting(false);
                    typeof e === 'string' && dispatch(showError(e));
                    console.error((e as Error).message);
                  }
                }}>
                {`Proceed ${isSubmitting ? '...' : ''}`}
              </Button>
            </Box>
          </>
        )}
      </Box>
      <Footer />
    </Box>
  );
}
